import type { QuerySpec, DateBucket } from '@/types/query';
import type { ChartOptions } from '@/types/widget';
import { useQueryResult } from '@/hooks/useQueryResult';
import { useEChartsInstance } from '@/hooks/useEChartsInstance';
import { useUiStore } from '@/store/ui-store';
import { ChartExportButton } from '@/features/export/ChartExportButton';
import { useChartRegistration } from '@/features/export/chart-registry';
import { WidgetContainer } from './WidgetContainer';
import { ReloadButton } from './ReloadButton';
import { CompareEmptyHint } from '@/features/compare/CompareEmptyHint';
import { useCategoryChartOption } from './use-category-chart-option';

type LineChartWidgetProps = {
  /** Widget id, used by the chart-registry for whole-dashboard PDF export. */
  widgetId: string;
  title: string;
  spec: QuerySpec;
  onRemove?: (() => void) | undefined;
  onSettings?: (() => void) | undefined;
  options?: ChartOptions | undefined;
  note?: string | undefined;
  seriesNames?: Record<string, string>;
  /** When the X axis is a bucketed date column, this is the bucket. */
  dateBucket?: DateBucket | undefined;
  /**
   * "Seri kırılımı" — when set, the chart pivots rows so each distinct
   * value of this column becomes its own coloured line instead of an
   * extra metric column.
   */
  breakdownBy?: string | undefined;
  /** Top-N series cap when `breakdownBy` is set. Defaults to 8. */
  breakdownLimit?: number | undefined;
  /**
   * User-chosen primary-axis row cap. Forwarded straight from
   * `widget.limit`. In breakdown mode the SQL LIMIT is bumped to a
   * safety cap so the user's row choice is applied client-side on the
   * pivot. `0` / `undefined` means "no cap".
   */
  primaryLimit?: number | undefined;
};

/**
 * Line chart. Uses the first groupBy column as X-axis and renders one
 * line per metric. All layout / pivot logic lives in
 * {@link useCategoryChartOption}; this shell only wires the props to
 * the hook and renders the {@link WidgetContainer} chrome.
 */
export function LineChartWidget({
  widgetId,
  title,
  spec,
  onRemove,
  onSettings,
  options,
  note,
  seriesNames,
  dateBucket,
  breakdownBy,
  breakdownLimit,
  primaryLimit,
}: LineChartWidgetProps): JSX.Element {
  const { data, isLoading, error, reload } = useQueryResult(spec);
  const theme = useUiStore((s) => s.theme);

  const { option, compareInfo } = useCategoryChartOption({
    kind: 'line',
    data,
    spec,
    theme,
    options,
    seriesNames,
    dateBucket,
    breakdownBy,
    breakdownLimit,
    primaryLimit,
  });

  const { chartRef, getInstance } = useEChartsInstance(option);
  useChartRegistration(widgetId, title, getInstance);

  return (
    <WidgetContainer
      title={title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      onSettings={onSettings}
      note={note}
      titleBarExtra={
        <div className="flex items-center gap-1.5">
          <ReloadButton onReload={reload} disabled={isLoading} />
          <ChartExportButton title={title} getInstance={getInstance} />
        </div>
      }
    >
      <div className="relative w-full h-full min-h-[200px]">
        <div ref={chartRef} className="w-full h-full" />
        {compareInfo && (
          <CompareEmptyHint
            hasA={compareInfo.hasA}
            hasB={compareInfo.hasB}
            labelA={compareInfo.meta.labelA}
            labelB={compareInfo.meta.labelB}
          />
        )}
      </div>
    </WidgetContainer>
  );
}
